import React, { useState, useEffect, useCallback, useRef } from 'react';
import client from '../../api/client';
import { uploadFile } from '../../utils/upload';
import { Settings, Upload, Save, X, Check } from 'lucide-react';
import { toast } from '../../components/Toast/toast';

export default function SettingsPage() {
  const [settings, setSettings] = useState({ bot_username: '', store_name: '', store_logo: '', store_subtitle: '', timezone_offset: 3 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await client.get('/settings/app');
      setSettings(res.data);
    } catch (err) {
      console.error('Ошибка загрузки настроек:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setSettings({ ...settings, store_logo: url });
    } catch {
      toast.error('Ошибка загрузки логотипа');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await client.put('/settings/app', {
        bot_username: settings.bot_username,
        store_name: settings.store_name,
        store_logo: settings.store_logo,
        store_subtitle: settings.store_subtitle,
        timezone_offset: Number(settings.timezone_offset),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
    } catch {
      toast.error('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6B9B74]"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-4 md:grid md:grid-cols-2 md:items-stretch md:gap-4 md:space-y-0">
      {/* Логотип */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0] md:order-1">
        <h3 className="text-sm font-semibold text-[#0f172a] mb-3">Логотип магазина</h3>
        {settings.store_logo && settings.store_logo !== '/logo.png' ? (
          <div className="relative inline-block">
            <img
              src={settings.store_logo}
              alt="Логотип"
              className="h-24 w-24 rounded-xl object-cover ring-1 ring-[#e2e8f0]"
            />
            <button
              onClick={() => setSettings({ ...settings, store_logo: '' })}
              className="absolute -top-2 -right-2 grid h-6 w-6 place-items-center rounded-full bg-[#D64545] text-white"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#e2e8f0] p-6 cursor-pointer transition hover:border-[#374151]/50 hover:bg-[#374151]/5">
            <Upload className="h-8 w-8 text-[#64748b]" />
            <span className="text-sm text-[#64748b]">
              {uploading ? 'Загрузка...' : 'Нажмите для загрузки'}
            </span>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
        )}
      </div>

      {/* Название */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0] md:order-2">
        <h3 className="text-sm font-semibold text-[#0f172a] mb-3">Название магазина</h3>
        <input
          type="text"
          value={settings.store_name}
          onChange={(e) => setSettings({ ...settings, store_name: e.target.value })}
          placeholder="So.Va"
          className="w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#374151] focus:ring-1 focus:ring-[#374151]/30"
        />
      </div>

      {/* Подзаголовок */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0] md:order-4">
        <h3 className="text-sm font-semibold text-[#0f172a] mb-3">Подзаголовок</h3>
        <input
          type="text"
          value={settings.store_subtitle}
          onChange={(e) => setSettings({ ...settings, store_subtitle: e.target.value })}
          placeholder="Садовый центр и Питомник растений"
          className="w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#374151] focus:ring-1 focus:ring-[#374151]/30"
        />
      </div>

      {/* Часовой пояс */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0] md:order-3">
        <h3 className="text-sm font-semibold text-[#0f172a] mb-3">Часовой пояс</h3>
        <select
          value={settings.timezone_offset}
          onChange={(e) => setSettings({ ...settings, timezone_offset: Number(e.target.value) })}
          className="w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#374151] focus:ring-1 focus:ring-[#374151]/30"
        >
          <option value={2}>UTC+2 — Калининград</option>
          <option value={3}>UTC+3 — Москва (МСК)</option>
          <option value={4}>UTC+4 — Самара</option>
          <option value={5}>UTC+5 — Екатеринбург</option>
          <option value={6}>UTC+6 — Омск</option>
          <option value={7}>UTC+7 — Красноярск</option>
          <option value={8}>UTC+8 — Иркутск</option>
          <option value={9}>UTC+9 — Якутск</option>
          <option value={10}>UTC+10 — Владивосток</option>
          <option value={11}>UTC+11 — Магадан</option>
          <option value={12}>UTC+12 — Камчатка</option>
        </select>
        <p className="mt-1.5 text-[10px] text-[#64748b]">Все даты в админке будут отображаться в этом часовом поясе.</p>
      </div>

      {/* Кнопка */}
      <button
        onClick={handleSave}
        disabled={saving || saved}
        className={`md:order-5 md:col-span-2 w-full flex items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-semibold shadow-sm transition active:scale-[0.98] disabled:opacity-50 ${
          saved
            ? 'bg-[#6B9B74]/15 text-[#6B9B74]'
            : 'bg-[#374151] text-white hover:bg-[#1f2937]'
        }`}
      >
        {saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
        {saving ? 'Сохранение...' : saved ? 'Сохранено!' : 'Сохранить'}
      </button>
    </div>
  );
}
